import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Divider,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login
} from '@mui/icons-material';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { API_URL } from '../config';

const LoginPage = ({ onLogin, onShowRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    setLoading(true);
    setError('');
    setResetSent(false);
    
    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Backend will check whitelist - no need to check here
      
      // Get Firebase ID token
      const idToken = await user.getIdToken();
      
      // Check if user has existing corpus_id from previous session
      const existingCorpusId = localStorage.getItem('corpus_id');
      
      // Backend login - validates whitelist, returns/creates corpus_id
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // Important: enables session cookies
        body: JSON.stringify({
          idToken: idToken,
          email: user.email,
          uid: user.uid,
          corpus_id: existingCorpusId  // Reuse existing corpus if available
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === 'UNAUTHORIZED_EMAIL') {
          // Specific error for unauthorized email
          await auth.signOut();
          setError('Access denied. Your email is not authorized. Contact support for access.');
          setLoading(false);
          return;
        }
        throw new Error(errorData.error || 'Backend login failed');
      }
      
      const loginResult = await response.json();
      console.log('Backend login successful:', loginResult);
      
      // Store corpus ID in localStorage for subsequent requests
      if (loginResult.corpus_id) {
        localStorage.setItem('corpus_id', loginResult.corpus_id);
        console.log('Corpus ID stored:', loginResult.corpus_id.substring(0, 8) + '...');
      } else {
        // Default to Skipper Demo if no corpus returned
        localStorage.setItem('corpus_id', 'demo-skipper-corpus-2024');
        console.log('✅ Set default corpus: Skipper Demo');
      }
      
      // Extract name from email
      const name = user.email.split('@')[0].split('.').map(
        word => word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      // Success - notify parent
      onLogin({
        name: name,
        email: user.email,
        company: 'Skipper inc.',
        role: 'Analyst',
        uid: user.uid
      });
      
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Try again or click "Forgot Password".');
      } else if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Contact admin for access.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later or reset your password.');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(error.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/assets/helm_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        p: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1
        }
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 420,
          width: '100%',
          borderRadius: 3,
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Helm Logo */}
        <Box sx={{ mb: 4 }}>
          <img 
            src="/assets/helm_logo.avif" 
            alt="Helm.ai" 
            style={{ height: 48, marginBottom: 16 }}
          />
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Marketing Intelligence
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
            Your operating system for better, faster decisions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to access your dashboard
          </Typography>
        </Box>

        {/* Success Alert */}
        {resetSent && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Password reset email sent! Check your inbox.
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleLogin}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              )
            }}
            sx={{ mb: 3 }}
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 3 }}
            required
            disabled={loading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={<Login />}
            sx={{ 
              mb: 3,
              py: 1.5,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Links */}
        <Box sx={{ textAlign: 'center' }}>
          <Link 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              handleForgotPassword();
            }}
            variant="body2" 
            sx={{ 
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Forgot your password?
          </Link>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Don't have an account?{' '}
            <Link 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onShowRegister();
              }}
              sx={{ 
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Create Account
            </Link>
          </Typography>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Register with any email - access requires approval
          </Typography>
        </Box>

      </Paper>
    </Box>
  );
};

export default LoginPage;
